import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navMenuValue, setNavMenuValue] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close navigation menu on route change
  useEffect(() => {
    setNavMenuValue('');
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Function to close the dropdown menu
  const closeNavMenu = () => {
    setNavMenuValue('');
  };

  const services = {
    ecommerce: {
      title: 'Ecommerce Marketplace Services',
      platforms: {
        amazon: {
          name: 'Amazon',
          services: [
            { name: 'Amazon Account Management Services', link: '/amazon-account-management-services' },
            { name: 'Amazon Product Listing & SEO Optimization', link: '/amazon-listing-seo-optimization' },
            { name: 'Amazon PPC Management & Advertising Services', link: '/amazon-ppc-advertising-services' },
            { name: 'Amazon A+ Content Design & Infographic Services', link: '/amazon-aplus-content-design' },
            { name: 'Amazon Brand Store Setup & Optimization', link: '/amazon-brand-store-setup' },
          ]
        },
        flipkart: {
          name: 'Flipkart',
          services: [
            { name: 'Flipkart Account Management Services', link: '/flipkart-account-management-services' },
            { name: 'Flipkart Product Listing & SEO Optimization', link: '/flipkart-listing-seo-optimization' },
            { name: 'Flipkart Advertising Management Services', link: '/flipkart-advertising-management' },
          ]
        },
        myntra: {
          name: 'Myntra',
          services: [
            { name: 'Myntra Product Listing & Catalog Optimization Services', link: '/myntra-listing-catalog-optimization' },
            { name: 'Myntra Account Management Services', link: '/myntra-account-management-services' },
          ]
        },
        ajio: {
          name: 'Ajio',
          services: [
            { name: 'Ajio Product Listing & Catalog Management', link: '/ajio-product-listing-catalog' },
            { name: 'Ajio Account Management Services', link: '/ajio-account-management-services' },
          ]
        }
      }
    },
    quickCommerce: {
      title: 'Quick Commerce Services',
      platforms: {
        blinkit: {
          name: 'Blinkit',
          services: [
            { name: 'Blinkit Seller Onboarding Services', link: '/blinkit-seller-onboarding' },
            { name: 'Blinkit Account Management & Optimization', link: '/blinkit-account-management' },
          ]
        },
        swiggyInstamart: {
          name: 'Swiggy Instamart',
          services: [
            { name: 'Swiggy Instamart Seller Onboarding', link: '/swiggy-instamart-seller-onboarding' },
            { name: 'Swiggy Instamart Account Management', link: '/swiggy-instamart-account-management' },
          ]
        },
        zomato: {
          name: 'Zomato',
          services: [
            { name: 'Zomato Seller Onboarding Services', link: '/zomato-seller-onboarding' },
            { name: 'Zomato Marketplace Account Management', link: '/zomato-account-management' },
          ]
        },
        swiggy: {
          name: 'Swiggy',
          services: [
            { name: 'Swiggy Seller Onboarding Services', link: '/swiggy-seller-onboarding' },
            { name: 'Swiggy Account Management & Growth', link: '/swiggy-account-management' },
          ]
        }
      }
    },
    local: {
      title: 'Local Search & Google Business Optimization',
      description: 'We help businesses improve their local visibility and strengthen their digital presence through strategic Google Business and local search optimization.',
      link: '/google-business-profile-optimization'
    },
    websiteDev: {
      title: 'Website Development',
      platforms: {
        wordpress: {
          name: 'WordPress',
          services: [
            { name: 'WordPress Website Development Services', link: '/wordpress-website-development' },
          ]
        },
        shopify: {
          name: 'Shopify',
          services: [
            { name: 'Shopify Store Development Services', link: '/shopify-store-development' },
          ]
        }
      }
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-md shadow-md'
            : 'bg-white/60 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between py-4 md:py-5 lg:py-6">
            <Link 
              to="/" 
              className="flex items-center flex-shrink-0" 
              data-testid="logo-link"
              onClick={() => window.scrollTo(0, 0)}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ecom-growth-hub-4/artifacts/bwt25vdc_logo%20sd%20%288%29%20%281%29.png"
                alt="Obiquiste Solutions"
                className="header-logo"
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8">
              <Link
                to="/"
                data-testid="nav-home"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === '/' ? 'text-primary' : 'text-slate-700'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                data-testid="nav-about"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === '/about' ? 'text-primary' : 'text-slate-700'
                }`}
              >
                About Us
              </Link>

              <NavigationMenu value={navMenuValue} onValueChange={setNavMenuValue}>
                <NavigationMenuList>
                  <NavigationMenuItem value="services">
                    <NavigationMenuTrigger
                      data-testid="nav-services-trigger"
                      className="text-sm font-medium text-slate-700 hover:text-primary"
                    >
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="mega-menu-container">
                        <div className="mega-menu-inner">
                          {/* Section 1: Ecommerce Marketplace Services */}
                          <div className="mega-menu-section">
                            <div className="mega-menu-section-header">
                              <span className="mega-menu-section-badge">MARKETPLACE</span>
                              <h3 className="mega-menu-section-title">Ecommerce Marketplace Services</h3>
                            </div>
                            <div className="mega-menu-grid">
                              {Object.values(services.ecommerce.platforms).map((platform, idx) => (
                                <div key={idx} className="mega-menu-column">
                                  <h4 className="mega-menu-platform-name">{platform.name}</h4>
                                  <ul className="mega-menu-list">
                                    {platform.services.map((service, sIdx) => (
                                      <li key={sIdx}>
                                        <Link
                                          to={service.link}
                                          onClick={closeNavMenu}
                                          className="mega-menu-link"
                                        >
                                          {service.name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Section 2: Quick Commerce Services */}
                          <div className="mega-menu-section mega-menu-section-alt">
                            <div className="mega-menu-section-header">
                              <span className="mega-menu-section-badge mega-menu-section-badge-alt">QUICK COMMERCE</span>
                              <h3 className="mega-menu-section-title">Quick Commerce Services</h3>
                            </div>
                            <div className="mega-menu-grid">
                              {Object.values(services.quickCommerce.platforms).map((platform, idx) => (
                                <div key={idx} className="mega-menu-column">
                                  <h4 className="mega-menu-platform-name">{platform.name}</h4>
                                  <ul className="mega-menu-list">
                                    {platform.services.map((service, sIdx) => (
                                      <li key={sIdx}>
                                        <Link
                                          to={service.link}
                                          onClick={closeNavMenu}
                                          className="mega-menu-link"
                                        >
                                          {service.name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Section 3: Website Development */}
                          <div className="mega-menu-section">
                            <div className="mega-menu-section-header">
                              <span className="mega-menu-section-badge" style={{background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)'}}>WEBSITE DEV</span>
                              <h3 className="mega-menu-section-title">Website Development</h3>
                            </div>
                            <div className="mega-menu-grid">
                              {Object.values(services.websiteDev.platforms).map((platform, idx) => (
                                <div key={idx} className="mega-menu-column">
                                  <h4 className="mega-menu-platform-name">{platform.name}</h4>
                                  <ul className="mega-menu-list">
                                    {platform.services.map((service, sIdx) => (
                                      <li key={sIdx}>
                                        <Link
                                          to={service.link}
                                          onClick={closeNavMenu}
                                          className="mega-menu-link"
                                        >
                                          {service.name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Section 4: Local Business */}
                          <Link
                            to={services.local.link}
                            onClick={closeNavMenu}
                            className="mega-menu-cta"
                          >
                            <div className="mega-menu-cta-content">
                              <span className="mega-menu-cta-badge">LOCAL SEO</span>
                              <h4 className="mega-menu-cta-title">{services.local.title}</h4>
                              <p className="mega-menu-cta-desc">{services.local.description}</p>
                            </div>
                            <ChevronDown className="mega-menu-cta-arrow" />
                          </Link>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              <Link
                to="/case-studies"
                data-testid="nav-case-studies"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname.includes('/case-studies') ? 'text-primary' : 'text-slate-700'
                }`}
              >
                Case Studies
              </Link>
              <Link
                to="/blog"
                data-testid="nav-blog"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname.includes('/blog') ? 'text-primary' : 'text-slate-700'
                }`}
              >
                Blog
              </Link>
                            <NavigationMenu value={navMenuValue} onValueChange={setNavMenuValue}>
                <NavigationMenuList>
                  <NavigationMenuItem value="tools">
                    <NavigationMenuTrigger
                      data-testid="nav-tools-trigger"
                      className={`text-sm font-medium hover:text-primary ${
                        location.pathname.includes('/tools') ? 'text-primary' : 'text-slate-700'
                      }`}
                    >
                      Tools
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-4 w-80">
                        <div className="space-y-1">
                          <Link
                            to="/tools/inventory-warehouse"
                            onClick={closeNavMenu}
                            className="flex items-start p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                          >
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900 text-sm group-hover:text-primary transition-colors">FBA Inventory Reconcile Tool</h4>
                              <p className="text-xs text-slate-500 mt-0.5">Upload DDS reports & get warehouse-wise inventory</p>
                            </div>
                          </Link>
                          <Link
                            to="/tools/amazon-revenue-calculator"
                            onClick={closeNavMenu}
                            className="flex items-start p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                          >
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900 text-sm group-hover:text-primary transition-colors">Amazon Revenue Calculator</h4>
                              <p className="text-xs text-slate-500 mt-0.5">Compare fees across all fulfillment modes</p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
              <Link
                to="/contact"
                data-testid="nav-contact"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === '/contact' ? 'text-primary' : 'text-slate-700'
                }`}
              >
                Contact Us
              </Link>
            </nav>

            <Link
              to="/contact?audit=true"
              data-testid="cta-request-audit"
              className="hidden lg:block bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-6 py-2.5 rounded-full text-sm"
            >
              Request Free Audit
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:text-primary"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-white"
            data-testid="mobile-menu"
          >
            <div className="pt-24 px-6 pb-6 overflow-y-auto h-full">
              <nav className="space-y-4">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-lg font-medium text-slate-900 hover:text-primary"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-lg font-medium text-slate-900 hover:text-primary"
                >
                  About Us
                </Link>
                
                {/* Services Section with Expandable Categories */}
                <div className="space-y-3">
                  <Link
                    to="/services"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-lg font-bold text-primary"
                  >
                    Services
                  </Link>
                  
                  {/* Amazon Services */}
                  <div className="pl-4 border-l-2 border-primary/20">
                    <p className="text-sm font-semibold text-slate-800 mb-2">Amazon</p>
                    <div className="space-y-2">
                      {services.ecommerce.platforms.amazon.services.map((service, idx) => (
                        <Link
                          key={idx}
                          to={service.link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-slate-600 hover:text-primary py-1"
                        >
                          {service.name.replace('Amazon ', '')}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Flipkart Services */}
                  <div className="pl-4 border-l-2 border-primary/20">
                    <p className="text-sm font-semibold text-slate-800 mb-2">Flipkart</p>
                    <div className="space-y-2">
                      {services.ecommerce.platforms.flipkart.services.map((service, idx) => (
                        <Link
                          key={idx}
                          to={service.link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-slate-600 hover:text-primary py-1"
                        >
                          {service.name.replace('Flipkart ', '')}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Myntra Services */}
                  <div className="pl-4 border-l-2 border-primary/20">
                    <p className="text-sm font-semibold text-slate-800 mb-2">Myntra</p>
                    <div className="space-y-2">
                      {services.ecommerce.platforms.myntra.services.map((service, idx) => (
                        <Link
                          key={idx}
                          to={service.link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-slate-600 hover:text-primary py-1"
                        >
                          {service.name.replace('Myntra ', '')}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Ajio Services */}
                  <div className="pl-4 border-l-2 border-primary/20">
                    <p className="text-sm font-semibold text-slate-800 mb-2">Ajio</p>
                    <div className="space-y-2">
                      {services.ecommerce.platforms.ajio.services.map((service, idx) => (
                        <Link
                          key={idx}
                          to={service.link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-slate-600 hover:text-primary py-1"
                        >
                          {service.name.replace('Ajio ', '')}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Quick Commerce */}
                  <div className="pl-4 border-l-2 border-secondary/20">
                    <p className="text-sm font-semibold text-slate-800 mb-2">Quick Commerce</p>
                    <div className="space-y-2">
                      {Object.values(services.quickCommerce.platforms).map((platform, idx) => (
                        <Link
                          key={idx}
                          to={platform.services[0].link}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-slate-600 hover:text-primary py-1"
                        >
                          {platform.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Local Search */}
                  <div className="pl-4 border-l-2 border-green-500/20">
                    <Link
                      to={services.local.link}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-sm font-semibold text-slate-800 hover:text-primary"
                    >
                      {services.local.title}
                    </Link>
                  </div>
                </div>

                <Link
                  to="/case-studies"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-lg font-medium text-slate-900 hover:text-primary"
                >
                  Case Studies
                </Link>
                <Link
                  to="/blog"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-lg font-medium text-slate-900 hover:text-primary"
                >
                  Blog
                </Link>
                
                {/* Tools Section */}
                <div className="space-y-3">
                  <p className="text-lg font-bold text-primary">Tools</p>
                  <div className="pl-4 border-l-2 border-primary/20 space-y-2">
                    <Link
                      to="/tools/inventory-warehouse"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-sm text-slate-600 hover:text-primary py-1"
                    >
                      FBA Inventory Reconcile Tool
                    </Link>
                    <Link
                      to="/tools/amazon-revenue-calculator"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-sm text-slate-600 hover:text-primary py-1"
                    >
                      Amazon Revenue Calculator
                    </Link>
                  </div>
                </div>

                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-lg font-medium text-slate-900 hover:text-primary"
                >
                  Contact Us
                </Link>
                <Link
                  to="/contact?audit=true"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block bg-primary text-white px-6 py-3 rounded-full font-semibold text-center mt-6"
                >
                  Request Free Audit
                </Link>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;